import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from 'react';
import { useIntl } from 'react-intl';

import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import {
  InboxAssistantFeedbackKey,
  PersonalProductivityEntryPointId,
  useFeedbackCollector,
} from '@trello/feedback-collector';
import { pauseShortcuts, resumeShortcuts } from '@trello/keybindings';
import {
  Spotlight,
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@trello/nachos/onboarding-experimental';
import { Popover, PopoverPlacement, usePopover } from '@trello/nachos/popover';
import { Tooltip } from '@trello/nachos/tooltip';
import type { WorkspaceNavigationTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { LazyInboxAssistant } from 'app/src/components/InboxAssistant';
import { IslandNav, IslandNavButton } from 'app/src/components/IslandNav';

import styles from './InboxAssistantIsland.less';

const INBOX_ASSISTANT_SPOTLIGHT_MESSAGE = 'inbox-assistant-spotlight';

interface InboxAssistantIslandProps {
  isInboxOpen: boolean;
  toggleInbox: () => void;
}

export const InboxAssistantIsland: FunctionComponent<
  InboxAssistantIslandProps
> = ({ isInboxOpen, toggleInbox }) => {
  const intl = useIntl();

  const [isInboxAssistantSpotlightActive, setIsInboxAssistantSpotlightActive] =
    useState(false);

  const { FeedbackCollector, showFeedbackCollector } = useFeedbackCollector({
    entrypointId: PersonalProductivityEntryPointId,
    source: 'inboxScreen',
    feedbackCollectorProps: {
      feedbackTitle: intl.formatMessage({
        id: 'templates.inbox.assistant-feedback-modal-title',
        defaultMessage: 'Share your thoughts on the Inbox Assistant',
        description:
          'A feedback window to provide feedback on the Inbox Assistant',
      }),
    },
    getFeedbackContextOverride: () =>
      `project-key: ${InboxAssistantFeedbackKey}`,
  });

  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();

  const inboxAssistantSpotlightDismissed = isOneTimeMessageDismissed(
    INBOX_ASSISTANT_SPOTLIGHT_MESSAGE,
  );

  const {
    targetRef: targetRefInboxAssistant,
    toggle: toggleInboxAssistant,
    triggerRef: triggerRefInboxAssistant,
    popoverProps: popoverPropsInboxAssistant,
    hide: hidePopoverInboxAssistant,
  } = usePopover({
    placement: PopoverPlacement.TOP_START,
  });

  const onFeedbackButtonClick = useCallback(() => {
    showFeedbackCollector();
    hidePopoverInboxAssistant();
  }, [hidePopoverInboxAssistant, showFeedbackCollector]);

  const openInboxAssistant = useCallback(() => {
    if (!isInboxOpen) {
      toggleInbox();
    }
    if (!inboxAssistantSpotlightDismissed) {
      dismissOneTimeMessage(INBOX_ASSISTANT_SPOTLIGHT_MESSAGE);
    }
    if (!popoverPropsInboxAssistant.isVisible) {
      Analytics.sendViewedComponentEvent({
        componentType: 'section',
        componentName: 'inboxAssistant',
        source: 'boardScreen',
      });
    }
    toggleInboxAssistant();
  }, [
    dismissOneTimeMessage,
    inboxAssistantSpotlightDismissed,
    isInboxOpen,
    popoverPropsInboxAssistant.isVisible,
    toggleInbox,
    toggleInboxAssistant,
  ]);

  const dismissInboxAssistantSpotlight = useCallback(async () => {
    setIsInboxAssistantSpotlightActive(false);
    await dismissOneTimeMessage(INBOX_ASSISTANT_SPOTLIGHT_MESSAGE);
  }, [setIsInboxAssistantSpotlightActive, dismissOneTimeMessage]);

  const handleInboxAssistantSpotlightOpen = useCallback(() => {
    openInboxAssistant();
    dismissInboxAssistantSpotlight();
  }, [dismissInboxAssistantSpotlight, openInboxAssistant]);

  const onNextFrame = (func: () => void, delay = 0): number => {
    const nextFrameTimeoutId = window.setTimeout(() => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(func));
    }, delay);
    return nextFrameTimeoutId;
  };

  useEffect(() => {
    if (!inboxAssistantSpotlightDismissed) {
      const nextFrameTimeoutId = onNextFrame(() => {
        setIsInboxAssistantSpotlightActive(true);
      }, 1000);
      return () => window.clearTimeout(nextFrameTimeoutId);
    }
  }, [inboxAssistantSpotlightDismissed]);

  useEffect(() => {
    if (isInboxAssistantSpotlightActive) {
      pauseShortcuts();
    } else if (!isInboxAssistantSpotlightActive) {
      resumeShortcuts();
    }

    return () => resumeShortcuts();
  }, [isInboxAssistantSpotlightActive]);

  return (
    <IslandNav
      ref={targetRefInboxAssistant}
      className={styles.container}
      testId={getTestId<WorkspaceNavigationTestIds>('inbox-assistant-island')}
    >
      <SpotlightManager blanketIsTinted>
        <SpotlightTarget name="inbox-assistant-button">
          <Tooltip content="Inbox Assistant" position="top">
            <IslandNavButton
              isSelected={popoverPropsInboxAssistant.isVisible}
              onClick={openInboxAssistant}
              ref={triggerRefInboxAssistant}
              title="Inbox Assistant"
              icon={<AtlassianIntelligenceIcon label="Inbox Assistant" />}
            />
          </Tooltip>
        </SpotlightTarget>
        <SpotlightTransition>
          {isInboxAssistantSpotlightActive && (
            <Spotlight
              target="inbox-assistant-button"
              targetRadius={4}
              key="inbox-assistant-button"
              dialogPlacement="left top"
              actions={[
                {
                  appearance: 'default',
                  onClick: handleInboxAssistantSpotlightOpen,
                  text: 'Open Inbox Assistant',
                },
                {
                  appearance: 'subtle',
                  onClick: dismissInboxAssistantSpotlight,
                  text: 'Dismiss',
                },
              ]}
            >
              <h4>Check out the new Inbox Assistant!</h4>
              Manage your inbox just by chatting with Taco, your very own AI
              assistant.
            </Spotlight>
          )}
        </SpotlightTransition>
      </SpotlightManager>
      <FeedbackCollector />
      <Popover
        {...popoverPropsInboxAssistant}
        noHorizontalPadding
        noVerticalPadding
        dangerous_width={400}
        placement="bottom-end"
      >
        <LazyInboxAssistant showFeedbackCollector={onFeedbackButtonClick} />
      </Popover>
    </IslandNav>
  );
};
